import * as React from "react";
import { Block } from "../Block";
import { getContextImage } from "./utils";
import { useImageContext } from "./useImageContext";
import { useStyletron } from "kanopi-components/theme/useStyletron";

type TImageAttributes = React.ImgHTMLAttributes<HTMLImageElement>;
type TBlockProps = React.ComponentProps<typeof Block>;
type TImageProps = {
  fluid?: boolean;
  src: string | string[];
  alt: string;
  sizes?: string;
  width?: number | string;
  height?: number | string;
  strictRatio?: boolean;
  [key: string]: unknown;
} & TImageAttributes &
  TBlockProps;

export function Img({ fluid, src, ...restProps }: TImageProps) {
  const fluidProps: TBlockProps = fluid && { maxWidth: "100%", height: "auto" };
  const context = useImageContext();
  const srcProps = getSrcProps(src, context);

  return (
    <Block
      as="img"
      $style={{ verticalAlign: "bottom" }}
      {...fluidProps}
      {...srcProps}
      {...restProps}
    />
  );
}

export function Image(props: TImageProps) {
  const { src, alt, sizes, width, height, strictRatio, ...restProps } = props;
  const [, theme] = useStyletron();
  const context = useImageContext();

  return (
    <Block overflow="hidden" {...restProps} height={height}>
      <Block width={width} height={height}>
        <img
          {...getSrcProps(src, context)}
          alt={getAlt(alt, src)}
          {...getSizeProps(sizes, theme)}
          width={strictRatio ? "100%" : null}
        />
      </Block>
    </Block>
  );
}

function getSrcProps(src, context): Pick<TImageAttributes, "src" | "srcSet"> {
  if (Array.isArray(src)) {
    const images = src.map(findImage(context));
    return {
      srcSet: images.join(","),
      src: images[images.length - 1],
    };
  }
  return {
    src: findImage(context)(src),
  };
}

function findImage(context) {
  return (src) => {
    if (src) {
      const match = /^(.*?)( \d+w)?$/g.exec(src);
      const filename = match[1];
      const width = match[2];
      const image = getContextImage(filename, context);
      if (image) {
        if (width) {
          return `${image} ${width}`;
        }
        return image;
      }
    }
    return null;
  };
}

function getAlt(alt, src) {
  if (alt) {
    return alt;
  }
  if (Array.isArray(src)) {
    return src[0];
  }
  return src;
}

function getSizeProps(sizes, theme) {
  if (Array.isArray(sizes)) {
    return {
      sizes: sizes
        .map((w, i) => getSizeBreakpoint(w, i, i === sizes.length - 1, theme))
        .filter(notNull)
        .join(","),
    };
  }
  if (sizes) {
    return {
      sizes,
    };
  }
  return {};
}

function getSizeBreakpoint(width, index, isLast, theme) {
  const sizeMap = ["small", "medium", "large", "default"];
  const size = isLast ? "default" : sizeMap[index];
  if (size) {
    const breakpoint = theme.breakpoints[size];
    if (breakpoint) {
      return `(max-width: ${breakpoint}px) ${width}`;
    }
    return width;
  }
  return null;
}

function notNull(value) {
  return value !== null;
}
